import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  PristineChangeEvent,
  StatusChangeEvent,
  TouchedChangeEvent,
  ValueChangeEvent,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { NeoAutocompleteV2Module } from '@neogov/pattern-library/neo-autocomplete-v2';
import { NeoButtonModule } from '@neogov/pattern-library/neo-button';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { NeoInputModule } from '@neogov/pattern-library/neo-input';
import { ButtonComponent } from '../button/button.component';
import { FieldErrorsComponent } from '../field-errors/field-errors.component';
import { IconComponent } from '../icon/icon.component';

/**
 * Autocomplete selection event for single-select autocomplete
 */
export interface SingleSelectAutocompleteEvent<T> {
  option: {
    value: T;
  };
}

/**
 * Autocomplete Field Component
 *
 * A single-select autocomplete field with search functionality.
 * Supports lazy loading, custom display functions, and validation.
 *
 * @example
 * ```html
 * <cm-autocomplete-field
 *   label="Employee Name"
 *   [options]="employees()"
 *   [loading]="loadingEmployees()"
 *   [displayFn]="employee => `${employee.firstName} ${employee.lastName}`"
 *   [trackByFn]="employee => employee.userId"
 *   [required]="true"
 *   autId="employee-autocomplete"
 *   formControlName="employeeId"
 *   (searchChange)="onEmployeeSearch($event)"
 *   (selectionChange)="onEmployeeSelected($event)"
 * />
 * ```
 *
 * @template T - The type of option items
 */
@Component({
  selector: 'cm-autocomplete-field',
  standalone: true,
  host: {
    '[class.has-visible-errors]': 'hasVisibleErrors()',
  },
  imports: [
    CommonModule,
    FormsModule,
    NeoFormFieldModule,
    NeoInputModule,
    NeoAutocompleteV2Module,
    NeoButtonModule,
    ButtonComponent,
    IconComponent,
    FieldErrorsComponent,
  ],
  templateUrl: './autocomplete-field.component.html',
  styleUrl: './autocomplete-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteFieldComponent),
      multi: true,
    },
  ],
})
export class AutocompleteFieldComponent<T> implements ControlValueAccessor, OnDestroy {
  /**
   * Label displayed above the field
   */
  public readonly label = input.required<string>();

  /**
   * Placeholder text
   */
  public readonly placeholder = input<string>('Search...');

  /**
   * Available options to select from (flat list)
   */
  public readonly options = input<T[]>([]);

  /**
   * Grouped options (alternative to flat options)
   * When provided, options will be displayed in groups with labels
   */
  public readonly optionGroups = input<{ label: string; options: T[] }[]>([]);

  /**
   * Whether options are loading
   */
  public readonly loading = input<boolean>(false);

  /**
   * Function to get display text for an option
   */
  public readonly displayFn = input.required<(option: T) => string>();

  /**
   * Function to get unique identifier for tracking
   */
  public readonly trackByFn = input.required<(option: T) => string | number>();

  /**
   * Whether the field is required
   */
  public readonly required = input<boolean>(false);

  /**
   * Hint text displayed below the field
   */
  public readonly hint = input<string>('');

  /**
   * Controls subscript (hint/error) sizing: 'fixed' reserves space, 'dynamic' collapses when empty
   */
  public readonly subscriptSizing = input<'fixed' | 'dynamic'>('fixed');

  /**
   * Automation test ID
   */
  public readonly autId = input.required<string>();

  /**
   * Whether to show the clear button when an option is selected
   * @default true
   */
  public readonly showClearButton = input<boolean>(true);

  /**
   * Custom error messages
   */
  public readonly errorMessages = input<Partial<Record<string, string>>>({});

  /**
   * When to show error messages
   */
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');

  /**
   * Whether to hide error messages
   */
  public readonly hideErrors = input<boolean>(false);

  /**
   * Optional template for rendering each dropdown option.
   * The option object is available as the implicit context variable.
   *
   * @example
   * ```html
   * <cm-autocomplete-field ...>
   *   <ng-template #optionTemplate let-option>
   *     <span>{{ option.firstName }} {{ option.lastName }}</span>
   *   </ng-template>
   * </cm-autocomplete-field>
   * ```
   */
  public readonly optionTemplate = contentChild<TemplateRef<{ $implicit: T }>>('optionTemplate');

  /**
   * Emitted when search input changes
   */
  public readonly searchChange = output<string>();

  /**
   * Emitted when an option is selected
   */
  public readonly selectionChange = output<T>();

  public readonly fieldBlur = output<void>();

  /**
   * Current search value
   */
  public searchValue = signal<string>('');

  /**
   * Tracks whether at least one search has been emitted so "No results found" shows
   * even when the search term is empty (e.g. after an initial pre-fetch).
   */
  public _hasSearched = signal<boolean>(false);

  /**
   * Currently selected option
   */
  public selectedOption = signal<T | null>(null);

  /**
   * Whether the component is disabled
   */
  public disabled = signal<boolean>(false);

  /**
   * Display text for selected option
   */
  public readonly selectedDisplayText = computed(() => {
    const option = this.selectedOption();
    if (!option) return '';
    return this.displayFn()(option);
  });

  /**
   * Whether grouped options are provided
   */
  public readonly hasGroups = computed(() => this.optionGroups().length > 0);

  /**
   * Passed to neo-autocomplete-v2 [displayWith] so the trigger writes
   * displayFn(option) — not option.toString() — into the native input on selection.
   */
  protected readonly _neoDisplayWith = computed(() => {
    const fn = this.displayFn();
    return (option: T | null) => (option ? fn(option) : '');
  });

  /**
   * All options flattened (for internal operations like finding by value)
   */
  protected readonly _allOptions = computed(() => {
    if (this.hasGroups()) {
      return this.optionGroups().flatMap(group => group.options);
    }
    return this.options();
  });

  private _toOptionVms(options: T[]): { option: T; key: string | number; label: string }[] {
    const displayFn = this.displayFn();
    const trackByFn = this.trackByFn();
    return options.map(option => ({ option, key: trackByFn(option), label: displayFn(option) }));
  }

  /**
   * Options with their display label and track key already resolved, so the template
   * never calls `displayFn()`/`trackByFn()` on every change detection cycle.
   */
  protected readonly optionVms = computed(() => this._toOptionVms(this.options()));

  /**
   * Grouped options with their display label and track key already resolved.
   */
  protected readonly optionGroupVms = computed(() =>
    this.optionGroups().map(group => ({
      label: group.label,
      options: this._toOptionVms(group.options),
    }))
  );

  /**
   * Internal signal to track control state changes.
   * Updated via subscriptions to control's events observable.
   */
  private readonly _controlState = signal<{
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
  } | null>(null);

  private _subscriptions: Subscription[] = [];

  /**
   * Whether the field has validation errors that should be displayed.
   * Used to apply the has-visible-errors class to neo-form-field.
   */
  public readonly hasVisibleErrors = computed(() => {
    const controlState = this._controlState();
    if (!controlState) return false;

    const showWhen = this.showErrorsWhen();
    const hasErrors = controlState.invalid;

    if (this.hideErrors() || !hasErrors) {
      return false;
    }

    // Match the logic from field-errors component
    switch (showWhen) {
      case 'always':
        return true;
      case 'dirty':
        return controlState.dirty;
      case 'touched':
      default:
        return controlState.touched;
    }
  });

  private readonly _injector = inject(Injector);
  public readonly ngControl = signal<NgControl | null>(null);

  /**
   * Stores the last written value for re-matching when options change
   */
  private _lastWrittenValue = signal<string | number | null>(null);

  /**
   * Set to true by onClear() to defer _onChange(null) until the panel closes.
   * This prevents a false "→ Unassigned" confirmation when the user clears and
   * then immediately selects a new option (blur fires before the click registers,
   * resolving the auto-save race with null before the new selection arrives).
   */
  private readonly _pendingClearNotify = signal<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string | number | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  constructor() {
    queueMicrotask(() => {
      try {
        const ngControl = this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });

    // Set up subscriptions to control state changes
    // Use effect to react when the ngControl signal changes
    effect(() => {
      const ngControl = this.ngControl();

      // Clean up previous subscriptions
      this._subscriptions.forEach((sub) => sub.unsubscribe());
      this._subscriptions = [];

      if (!ngControl?.control) {
        this._controlState.set(null);
        return;
      }

      const control = ngControl.control;

      // Subscribe to ALL control state changes via events observable
      // This captures touched, dirty, status, and value changes
      const eventsSub = control.events?.subscribe((event) => {
        if (
          event instanceof TouchedChangeEvent ||
          event instanceof PristineChangeEvent ||
          event instanceof StatusChangeEvent ||
          event instanceof ValueChangeEvent
        ) {
          this._updateControlState();
        }
      });

      if (eventsSub) {
        this._subscriptions.push(eventsSub);
      }

      // Initial state update
      this._updateControlState();
    });

    // Re-match the selected option whenever options OR lastWrittenValue change
    // This handles cases where:
    // 1. writeValue is called before options are loaded
    // 2. writeValue is called with a new value after options are already loaded
    effect(() => {
      const allOptions = this._allOptions();
      const lastValue = this._lastWrittenValue();

      if (lastValue !== null && lastValue !== undefined && allOptions.length > 0) {
        const option = allOptions.find((opt) => this.trackByFn()(opt) === lastValue);
        if (option) {
          // Always update even if it's the same reference, in case internal state is stale
          this.selectedOption.set(option);
        }
      } else if (lastValue === null || lastValue === undefined) {
        // Clear selection when value is explicitly set to null
        this.selectedOption.set(null);
      }
    });
  }

  public writeValue(value: string | number | null): void {
    // Store the value for re-matching when options load
    this._lastWrittenValue.set(value);

    if (value !== null && value !== undefined) {
      // Find the option that matches this value from all options (flat or grouped)
      const option = this._allOptions().find((opt) => this.trackByFn()(opt) === value);
      this.selectedOption.set(option || null);
    } else {
      this.selectedOption.set(null);
    }
  }

  public registerOnChange(fn: (value: string | number | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /**
   * Handles search input changes
   * Clears the selected option when user starts typing to allow new search
   */
  public onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.searchValue.set(value);
    this._hasSearched.set(true);

    // Clear selection when user starts typing a new search
    if (this.selectedOption()) {
      this.selectedOption.set(null);
      this._lastWrittenValue.set(null);
      this._onChange(null);
    }

    this.searchChange.emit(value);
  }

  /**
   * Handles autocomplete panel closed event
   * Resets search state when panel closes to show full list on next open
   */
  public onPanelClosed(): void {
    // If the user clicked X but then selected an option, _pendingClearNotify was
    // already cleared by onOptionSelected(). If they cleared without selecting,
    // we notify the form of the null value here and emit fieldBlur so the
    // widget's auto-save pipeline race (created by the _onChange(null) call below)
    // resolves immediately — avoiding a race stuck waiting forever.
    if (this._pendingClearNotify()) {
      this._pendingClearNotify.set(false);
      if (!this.selectedOption()) {
        this._onChange(null);
        this.fieldBlur.emit();
      }
    }

    // Reset search when panel closes (not when it opens) to avoid feedback loops
    if (this.searchValue() !== '' || this._hasSearched()) {
      this.searchValue.set('');
      this._hasSearched.set(false);
      // Notify parent to reset any search-based filtering
      this.searchChange.emit('');
    }
  }

  /**
   * Handles option selection
   */
  public onOptionSelected(event: SingleSelectAutocompleteEvent<T> | null | undefined): void {
    if (!event || !event.option || event.option.value === undefined) {
      return;
    }

    const option = event.option.value;
    const value = this.trackByFn()(option);

    this._pendingClearNotify.set(false); // selection made; cancel deferred clear-notify
    this.selectedOption.set(option);

    // Same-value re-selection: the selected id matches what the form already holds
    // (_lastWrittenValue tracks the last writeValue call). Nothing has changed —
    // skip onChange / selectionChange so the widget pipeline is not triggered.
    if (value === this._lastWrittenValue()) {
      this.searchValue.set('');
      this._hasSearched.set(false);
      this.searchChange.emit('');
      return;
    }

    this._lastWrittenValue.set(value);
    this._onChange(value);
    this._onTouched();

    this.selectionChange.emit(option);

    // Clear search state (panel will close, and onPanelClosed will handle reset)
    this.searchValue.set('');
    this._hasSearched.set(false);

    // Notify parent to reset any search-based filtering
    this.searchChange.emit('');
  }

  /**
   * Handles input blur
   */
  public onInputBlur(): void {
    // When requireSelection resets the DOM input to '' on blur (no valid selection),
    // sync the searchValue signal so the panel state is consistent on next open.
    if (!this.selectedOption()) {
      this.searchValue.set('');
      this._hasSearched.set(false);
    }
    this._onTouched();
    this.fieldBlur.emit();
  }

  /**
   * Clears the selection
   */
  public onClear(): void {
    if (this.disabled()) {
      return;
    }
    this.selectedOption.set(null);
    this.searchValue.set('');
    this._hasSearched.set(false);
    this._lastWrittenValue.set(null);
    // Do NOT call _onChange(null) here. If we call it now, the widget's auto-save
    // pipeline creates a race that resolves via blur (which the browser fires on
    // mousedown of the dropdown option, before the click registers). That produces a
    // false "→ Unassigned" confirmation when the user clears and immediately picks
    // someone. _onChange(null) is deferred to onPanelClosed() instead.
    this._pendingClearNotify.set(true);
    this._onTouched();
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Updates the internal control state signal with current values from the control.
   */
  private _updateControlState(): void {
    const control = this.ngControl()?.control;
    if (control) {
      this._controlState.set({
        invalid: control.invalid,
        touched: control.touched,
        dirty: control.dirty,
      });
    }
  }
}
